import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Box, Grid, Button, TextField, Chip } from '@mui/material';
import { Icon } from '@iconify/react';
import Loader from '../../../components/loader';
import { cropsGetById, listIntegratorsByCrop } from '../crops.service';
import {
  FieldLabel,
  formFieldSx,
  sectionTitleSx,
} from '../../../styles/formStyles';
import {
  createEditBackButtonSx,
  createEditCardSx,
  createEditPageHeaderSx,
  createEditPageShellSx,
  createEditPageTitleSx,
} from '../../../styles/pageFormStyles';

const fieldSx = formFieldSx;

function DisplayField({ label, value, multiline = false, minRows = 1 }) {
  return (
    <Box>
      <FieldLabel label={label} />
      <TextField
        value={value || '-'}
        fullWidth
        multiline={multiline}
        minRows={minRows}
        sx={fieldSx}
        InputProps={{
          readOnly: true,
        }}
      />
    </Box>
  );
}

function CropsView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [cropData, setCropData] = useState({});
  const [integrators, setIntegrators] = useState([]);
  const [loading, setLoading] = useState(false);

  const getSafeValue = (value) => {
    if (value === null || value === undefined) return '-';
    const text = String(value).trim();
    return text || '-';
  };

  const getCropData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await cropsGetById(id);
      const data = response?.id ? response : response?.data || {};
      setCropData(data);

      // Fetch linked integrators
      const integratorsResponse = await listIntegratorsByCrop(id);
      setIntegrators(Array.isArray(integratorsResponse) ? integratorsResponse : []);
    } catch (error) {
      console.error('Error fetching crop by ID:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getCropData();
  }, [getCropData]);

  if (loading) {
    return <Loader />;
  }

  return (
    <Box sx={createEditPageShellSx}>
      <Box sx={createEditPageHeaderSx}>
        <Button
          onClick={() => navigate('/crops')}
          startIcon={<Icon icon="mdi:arrow-left" width="20" height="20" />}
          sx={createEditBackButtonSx}
        >
          Back
        </Button>
        <Grid container alignItems="center" spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Typography className="app-page-title" sx={createEditPageTitleSx}>
              Crop Details
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} textAlign={{ xs: 'left', md: 'right' }}>
            <Button
              onClick={() => navigate(`/addCrops/${id}?type=edit`)}
              startIcon={<span className="material-symbols-rounded">edit</span>}
              sx={{
                minWidth: { xs: '100%', sm: 86 },
                height: 40,
                borderRadius: '12px',
                backgroundColor: '#DFE5F6',
                color: '#294190',
                textTransform: 'none',
                fontSize: 16,
                lineHeight: '24px',
                letterSpacing: '0.15px',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#DFE5F6',
                },
                '& .material-symbols-rounded': {
                  fontSize: 18,
                },
              }}
            >
              Edit
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box sx={createEditCardSx}>
        <Typography sx={sectionTitleSx}>
          Crop Details
        </Typography>

        <Grid container spacing={3} alignItems="flex-start">
          <Grid size={12}>
            <DisplayField
              label="Crop Name"
              value={getSafeValue(cropData?.name)}
            />
          </Grid>

          <Grid size={12}>
            <DisplayField
              label="Category"
              value={getSafeValue(cropData?.category?.replace(/_/g, ' '))}
            />
          </Grid>

          <Grid size={12}>
            <DisplayField
              label="Description"
              value={getSafeValue(cropData?.description)}
              multiline
              minRows={3}
            />
          </Grid>

          <Grid size={12}>
            <DisplayField
              label="Status"
              value={cropData?.status === 'ACTIVE' ? 'Active' : cropData?.status === 'INACTIVE' ? 'Inactive' : getSafeValue(cropData?.status)}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={createEditCardSx}>
        <Typography sx={sectionTitleSx}>
          Linked Ecosystem Integrators
        </Typography>

        <Grid container spacing={3} alignItems="flex-start">
          <Grid size={12}>
            {integrators && integrators.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {integrators.map((integrator) => (
                  <Chip
                    key={integrator.ecosystemIntegratorId}
                    label={integrator.integratorName || integrator.ecosystemIntegratorId}
                    variant="outlined"
                  />
                ))}
              </Box>
            ) : (
              <Typography sx={{ color: '#625B71', fontSize: 14 }}>
                No ecosystem integrators linked to this crop
              </Typography>
            )}
          </Grid>
          <Grid size={12}>
            <Button
              onClick={() => navigate(`/addCrops/${id}?type=edit#integrators`)}
              startIcon={<span className="material-symbols-rounded">add</span>}
              sx={{
                minWidth: { xs: '100%', sm: 120 },
                height: 40,
                borderRadius: '12px',
                backgroundColor: '#DFE5F6',
                color: '#294190',
                textTransform: 'none',
                fontSize: 14,
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#DFE5F6',
                },
              }}
            >
              Link Integrators
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

export default CropsView;
